import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions'; 
import type { InventoryFilters, InventoryItem, InventoryListResult, InventoryStats, InventoryStatus } from '../types/inventoryList.types';

type InventoryRow = Record<string, unknown>;

const emptyStats: InventoryStats = {
  totalItems: 0,
  totalValue: 0,
  lowStockCount: 0,
  outOfStockCount: 0,
  activeCount: 0,
  inactiveCount: 0,
};

function numberValue(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function textValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

export function resolveInventoryStatus(row: InventoryRow): InventoryStatus {
  const rawStatus = textValue(row.status_code);
  const isInactive = rawStatus === 'inactive' || rawStatus === 'disabled' || row.is_active === false;
  if (isInactive) return 'inactive';

  const stock = numberValue(row.current_stock ?? row.stock_quantity ?? row.quantity);
  const minimum = numberValue(row.minimum_stock ?? row.min_stock ?? 20);
  if (stock <= 0) return 'out_of_stock';
  if (stock <= minimum) return 'low_stock';
  return 'in_stock';
}

export function mapInventoryItem(row: InventoryRow): InventoryItem {
  const currentStock = numberValue(row.current_stock ?? row.stock_quantity ?? row.quantity);
  const unitPrice = numberValue(row.unit_price ?? row.purchase_price ?? row.last_unit_cost);

  return {
    id: textValue(row.id),
    company_id: textValue(row.company_id) || null,
    code: textValue(row.code, textValue(row.item_code, 'VT-000')),
    name: textValue(row.name, textValue(row.item_name, 'Vật tư chưa đặt tên')),
    sub_name: textValue(row.sub_name, textValue(row.description)) || null,
    category_code: textValue(row.category_code) || null,
    category_name: textValue(row.category_name, textValue(row.group_name, 'Vật tư')) || null,
    group_id: textValue(row.group_id, textValue(row.category_id)) || null,
    unit: textValue(row.unit, 'Cái'),
    current_stock: currentStock,
    minimum_stock: numberValue(row.minimum_stock ?? row.min_stock ?? 20),
    unit_price: unitPrice,
    status_code: resolveInventoryStatus(row),
    thumbnail_url: textValue(row.thumbnail_url, textValue(row.image_url)) || null,
    supplier_name: textValue(row.supplier_name, textValue(row.vendor_name)) || null,
    specification: textValue(row.specification, textValue(row.sub_name, textValue(row.description))) || null,
    warranty_note: textValue(row.warranty_note, textValue(row.warranty)) || null,
    warehouse_name: textValue(row.warehouse_name, textValue(row.location_name, 'Kho vật tư chính')) || null,
    storage_location: textValue(row.storage_location, textValue(row.location)) || null,
    note: textValue(row.note, textValue(row.notes)) || null,
    updated_at: textValue(row.updated_at) || null,
  };
}

function calculateStats(items: InventoryItem[]): InventoryStats {
  return {
    totalItems: items.length,
    totalValue: items.reduce((sum, item) => sum + item.current_stock * item.unit_price, 0),
    lowStockCount: items.filter((item) => item.status_code === 'low_stock').length,
    outOfStockCount: items.filter((item) => item.status_code === 'out_of_stock').length,
    activeCount: items.filter((item) => item.status_code !== 'inactive').length,
    inactiveCount: items.filter((item) => item.status_code === 'inactive').length,
  };
}

function applyClientFilters(items: InventoryItem[], filters: InventoryFilters) {
  return items.filter((item) => {
    if (filters.status !== 'all' && item.status_code !== filters.status) return false;
    
    if (filters.category && item.group_id !== filters.category && item.category_name !== filters.category && item.category_code !== filters.category) return false;
    if (filters.warehouse && item.warehouse_name !== filters.warehouse) return false;
    if (filters.supplier && item.supplier_name !== filters.supplier) return false;
    
    // For stockStatus
    if (filters.stockStatus) {
      if (filters.stockStatus === 'Tồn > 100' && item.current_stock <= 100) return false;
      if (filters.stockStatus === 'Tồn thấp' && item.status_code !== 'low_stock') return false;
      if (filters.stockStatus === 'Tồn âm' && item.current_stock >= 0) return false;
      if (filters.stockStatus === 'Không phát sinh' && item.current_stock !== 0) return false;
    }
    
    // For itemType
    if (filters.itemType) {
      const typeStr = item.category_name?.toLowerCase() || '';
      if (filters.itemType === 'Vật tư tiêu hao' && !typeStr.includes('tiêu hao')) return false;
      if (filters.itemType === 'Vật tư cố định' && !typeStr.includes('cố định')) return false;
      if (filters.itemType === 'Vật tư thay thế' && !typeStr.includes('thay thế')) return false;
    }
    
    return true;
  });
}

export const inventoryListService = {
  async getInventoryList(companyId: string, filters: InventoryFilters, userId: string): Promise<InventoryListResult> {
    if (!companyId || !userId) {
      return { 
        items: [], 
        stats: emptyStats, 
        total: 0,
        filterOptions: { categories: [], warehouses: [], suppliers: [] }
      };
    }

    await permissionEngine.assert(userId, 'inventory.items.view', { companyId });

    let query = supabase
      .from('inventory_items')
      .select('*')
      .or(`company_id.eq.${companyId},company_id.is.null`);

    if (filters.search.trim()) {
      const search = filters.search.trim().replaceAll(',', ' ');
      query = query.or(`name.ilike.%${search}%,code.ilike.%${search}%,category_code.ilike.%${search}%,unit.ilike.%${search}%`);
    }

    const { data, error } = await query.order('name', { ascending: true });
    if (error) throw error;

    const allItems = ((data ?? []) as InventoryRow[]).map(mapInventoryItem);
    const stats = calculateStats(allItems);
    
    // Extract dynamic options
    const categories = Array.from(new Set(allItems.map(i => i.category_name).filter(Boolean))) as string[];
    const warehouses = Array.from(new Set(allItems.map(i => i.warehouse_name).filter(Boolean))) as string[];
    const suppliers = Array.from(new Set(allItems.map(i => i.supplier_name).filter(Boolean))) as string[];
    
    categories.sort();
    warehouses.sort();
    suppliers.sort();

    const filteredItems = applyClientFilters(allItems, filters);
    const from = (filters.page - 1) * filters.pageSize;
    const to = from + filters.pageSize;

    return {
      items: filteredItems.slice(from, to),
      stats,
      total: filteredItems.length,
      filterOptions: {
        categories,
        warehouses,
        suppliers
      }
    };
  },

  async deleteInventoryItem(id: string, companyId: string, userId: string): Promise<void> {
    await permissionEngine.assert(userId, 'inventory.items.delete', { companyId, inventoryItemId: id });

    const { error } = await supabase
      .from('inventory_items')
      .delete()
      .eq('id', id)
      .eq('company_id', companyId);

    if (error) throw error;
  },

  async getGroups(companyId: string) {
    const { data, error } = await supabase
      .from('inventory_item_groups')
      .select('id, name, parent_id, level, path')
      .eq('company_id', companyId)
      .order('sort_order');
    if (error) throw error;
    return data || [];
  },
};
